// src/migration/flowMigration.js
//
// Propuesta de paso para los reclamos y cheques que existen desde antes del
// motor de pasos (PLAN_ESTRUCTURA_PASOS.md, Etapa 2). Funciones puras: no
// leen ni escriben Firestore — FlowMigration.jsx les pasa los datos y arma
// el Excel del reporte. Aplicar (services/migrationService.js) es un paso
// aparte, con confirmación, después de revisar el Excel.
//
// Reglas acordadas (2026-09-23):
//   reclamo con cheques abiertos            → collection   (Cobro en curso)
//   reclamo con cheques, todos archivados   → closed       (Cerrado)
//   sin cheques, con número de reclamo      → awaiting_coverage
//   sin cheques ni número de reclamo        → prospect
//   cheque: tramo A completo como "legado"; tramo B según su etapa real.
// Nunca se inventa la fecha de entrada al paso: queda `enteredAt: null` y
// `migrated: true`, para que las futuras alertas no se disparen en falso.
import { getStep } from '../services/flowService';
import { CLAIM_FLOW } from '../config/claimFlow';
import { buildLorData } from '../documents/lorData';
import { buildSpolData } from '../documents/spolData';
import { isCheckReadyForPayment } from '../utils/claimHelpers';

const has = v => v !== null && v !== undefined && String(v).trim() !== '';

// Datos que pide el estimado del PA: no se consideran "faltantes" de un
// reclamo migrado, se cargan recién en el paso RS / POL.
const ESTIMATE_KEYS = new Set(['actualCashValue', 'claimed']);

export const proposeClaimStep = (claimRecord, checks = []) => {
  const open = checks.filter(check => !check.archived);
  if (checks.length > 0 && open.length > 0) return { stepId: 'collection', reason: `${open.length} cheque(s) abierto(s)` };
  if (checks.length > 0) return { stepId: 'closed', reason: 'Todos los cheques archivados' };
  if (has(claimRecord.claimNumber)) return { stepId: 'awaiting_coverage', reason: 'Sin cheques, con número de reclamo' };
  return { stepId: 'prospect', reason: 'Sin cheques ni número de reclamo' };
};

// Tramo B del cheque según su estado real (misma lógica que "Claim Status").
export const proposeCheckStep = check => {
  // Comisión cobrada (pagada, con o sin el viejo "archivar") = cheque terminado.
  if (check.archived || check.paid) return 'check_archived';
  if (isCheckReadyForPayment(check)) return 'csr_payment';
  return 'real_values';
};

// Lo que le falta al reclamo para generar LOR y SPOL (sin los montos del estimado).
export const claimMissingData = (claimRecord, language = 'es') => {
  const labels = [
    ...buildLorData(claimRecord, language).missing,
    ...buildSpolData(claimRecord, {}, language).missing.filter(m => !ESTIMATE_KEYS.has(m.key))
  ].map(m => m.label);
  return Array.from(new Set(labels));
};

const stepLabel = (type, stepId, language) => getStep(type, stepId)?.label[language === 'es' ? 'es' : 'en'] || stepId;

// Reporte completo: una fila por reclamo y una por cheque.
export const buildMigrationReport = (claimRecordsWithChecks, language = 'es') => {
  const claims = [];
  const checks = [];
  claimRecordsWithChecks.forEach(record => {
    const recordChecks = record.checks || [];
    const alreadyInFlow = Boolean(record.flow?.stepId);
    const proposal = alreadyInFlow ? { stepId: record.flow.stepId, reason: 'Ya tiene paso (no se toca)' } : proposeClaimStep(record, recordChecks);
    const missing = claimMissingData(record, language);
    claims.push({
      id: record.id,
      alreadyInFlow,
      clientName: record.clientName || '',
      claimNumber: record.claimNumber || '',
      companyName: record.companyName || '',
      adjusterName: record.adjusterName || '',
      checks: recordChecks.length,
      openChecks: recordChecks.filter(c => !c.archived).length,
      stepId: proposal.stepId,
      stepLabel: stepLabel('claim', proposal.stepId, language),
      reason: proposal.reason,
      missingCount: missing.length,
      missing: missing.join(', ')
    });
    // Los cheques de la Fase 4 ya nacen con su flujo: no se tocan.
    recordChecks.filter(check => !check.flow?.stepId).forEach(check => {
      const stepId = proposeCheckStep(check);
      checks.push({
        id: check.id,
        claimRecordId: record.id,
        clientName: record.clientName || '',
        invoiceNumber: check.invoiceNumber || '',
        amount: check.amount ?? check.checkAmount ?? '',
        stepId,
        stepLabel: stepLabel('check', stepId, language)
      });
    });
  });
  const summary = claims.reduce((acc, row) => {
    acc[row.stepLabel] = (acc[row.stepLabel] || 0) + 1;
    return acc;
  }, {});
  return { claims, checks, summary };
};

// ── Aplicar (con aprobación) ─────────────────────────────────────────────

// Paso escrito a mano en la columna "Paso corregido (opcional)" del Excel:
// vale el id del paso o su nombre en español o inglés.
const findClaimStepId = value => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return null;
  const step = CLAIM_FLOW.steps.find(s => s.id === text || s.label.es.toLowerCase() === text || s.label.en.toLowerCase() === text);
  return step ? step.id : undefined;
};

// Aplica las correcciones del Excel revisado al reporte. Devuelve el reporte
// nuevo, cuántas filas se corrigieron y las que no se entendieron.
export const applyCorrections = (report, rows, language = 'es') => {
  const byId = new Map((rows || []).map(row => [String(row['ID reclamo'] || '').trim(), row['Paso corregido (opcional)']]));
  let corrected = 0;
  const invalid = [];
  const claims = report.claims.map(row => {
    if (row.alreadyInFlow || !byId.has(row.id)) return row;
    const stepId = findClaimStepId(byId.get(row.id));
    if (stepId === null) return row;
    if (stepId === undefined) { invalid.push(`${row.clientName || row.id}: "${byId.get(row.id)}"`); return row; }
    corrected += 1;
    return { ...row, stepId, stepLabel: stepLabel('claim', stepId, language), reason: 'Corregido en el Excel' };
  });
  return { report: { ...report, claims }, corrected, invalid };
};

// Escrituras de la migración: solo reclamos y cheques SIN paso. Nunca se
// inventa la fecha de entrada (enteredAt: null, migrated: true) para que los
// plazos no alerten en falso; cada una deja su entrada en el historial.
// Escritura de "paso migrado" (flow + entrada de historial) que aplica
// services/migrationService.js — la usa también la importación de Organized.
export const migratedStepWrite = (collectionName, id, type, stepId, byEmail, now, reason) => {
  const step = getStep(type, stepId);
  return {
    collection: collectionName,
    id,
    flow: { type, stepId, phase: step.phase, actor: step.actor, enteredAt: null, updatedBy: byEmail, updatedByRole: 'admin', lastAction: 'migrated', migrated: true },
    history: { fromStep: null, toStep: stepId, decision: null, reason, action: 'migrated', byEmail, byRole: 'admin', at: now }
  };
};

export const buildMigrationWrites = (report, byEmail, now) => {
  const flowFor = (type, stepId) => migratedStepWrite('', '', type, stepId, byEmail, now, '').flow;
  const history = (stepId, reason) => ({ fromStep: null, toStep: stepId, decision: null, reason, action: 'migrated', byEmail, byRole: 'admin', at: now });
  return [
    ...report.claims.filter(r => !r.alreadyInFlow).map(r => ({ collection: 'claimRecords', id: r.id, flow: flowFor('claim', r.stepId), history: history(r.stepId, `Migración: ${r.reason}`) })),
    ...report.checks.map(r => ({ collection: 'checks', id: r.id, flow: flowFor('check', r.stepId), history: history(r.stepId, 'Migración: cheque anterior al flujo (tramo A completo)') }))
  ];
};
